import { Navigate, type RouteObject } from "react-router-dom";
/* eslint-disable react-refresh/only-export-components */
import { AppShell } from "@/components/layout/AppShell";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/home/page";
import Dashboard from "@/pages/dashboard/page";
import Products from "@/pages/products/page";
import ProductDetail from "@/pages/products/detail/page";
import CreateProduct from "@/pages/products/create/page";
import Inventory from "@/pages/inventory/page";
import Locations from "@/pages/locations/page";
import LocationDetail from "@/pages/locations/detail/page";
import Suppliers from "@/pages/suppliers/page";
import SupplierDetail from "@/pages/suppliers/detail/page";
import PurchaseOrders from "@/pages/purchase-orders/page";
import PurchaseOrderDetail from "@/pages/purchase-orders/detail/page";
import SalesOrders from "@/pages/sales-orders/page";
import SalesOrderDetail from "@/pages/sales-orders/detail/page";
import Warehouse from "@/pages/warehouse/page";
import PickSession from "@/pages/warehouse/pick/page";
import BarcodeLookup from "@/pages/warehouse/lookup/page";
import ShipmentDetail from "@/pages/shipments/detail/page";
import PackingVerification from "@/pages/warehouse/packing/page";
import { RedirectLegacyShipments, RedirectLegacyWarehouse } from "@/pages/fulfilment/redirects";
import Customers from "@/pages/customers/page";
import CustomerDetail from "@/pages/customers/detail/page";
import Transfers from "@/pages/transfers/page";
import TransferDetail from "@/pages/transfers/detail/page";
import Returns from "@/pages/returns/page";
import ReturnDetail from "@/pages/returns/detail/page";
import Audit from "@/pages/audit/page";
import Reports from "@/pages/reports/page";
import OrganizationSettings from "@/pages/settings/organization/page";
import UsersSettings from "@/pages/settings/users/page";
import RolesSettings from "@/pages/settings/roles/page";
import AuthCallback from "@/pages/auth/callback/page";
import Login from "@/pages/login/page";
import Register from "@/pages/register/page";
import ForgotPassword from "@/pages/forgot-password/page";
import ResetPassword from "@/pages/reset-password/page";
import PrivacyPolicyPage from "@/pages/privacy/page";
import Connectors from "@/pages/integrations/page";
import Carriers from "@/pages/integrations/carriers/page";
import Automation from "@/pages/integrations/automation/page";
import ChannelAllocations from "@/pages/integrations/channels/page";
import IntegrationExceptions from "@/pages/integrations/exceptions/page";
import AiControlCentre from "@/pages/settings/ai/page";
import Notifications from "@/pages/notifications/page";
import ProfileSettings from "@/pages/settings/profile/page";
import PreferencesSettings from "@/pages/settings/preferences/page";
import Listings from "@/pages/listings/page";
import ListingDetail from "@/pages/listings/detail/page";
import ListingTemplates from "@/pages/listing-templates/page";
import ListingTemplatePreview from "@/pages/listing-templates/preview/page";
import ListingTemplateReview from "@/pages/listing-templates/review/page";
import EbayConnectorConfig from "@/pages/integrations/ebay/page";
import BigCommerceConnectorConfig from "@/pages/integrations/bigcommerce/page";
import Replenishment from "@/pages/replenishment/page";
import Stocktake from "@/pages/stocktake/page";
import ReconciliationCentre from "@/pages/reconciliation/page";
import ExceptionCentre from "@/pages/exceptions/page";
import AutomationRules from "@/pages/automation/page";
import { RouteGuard } from "@/components/feature/RouteGuard";
import { isAdmin, hasRole } from "@/lib/permissions";

const AppLayout = () => (
  <AppShell>
    <Home />
  </AppShell>
);

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppLayout />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
  },
  {
    path: "/register",
    element: <Register />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/privacy",
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/dashboard",
    element: (
      <AppShell>
        <Dashboard />
      </AppShell>
    ),
  },
  {
    path: "/products",
    element: (
      <AppShell>
        <Products />
      </AppShell>
    ),
  },
  {
    path: "/products/new",
    element: (
      <AppShell>
        <CreateProduct />
      </AppShell>
    ),
  },
  {
    path: "/products/:id",
    element: (
      <AppShell>
        <ProductDetail />
      </AppShell>
    ),
  },
  {
    path: "/inventory",
    element: (
      <AppShell>
        <Inventory />
      </AppShell>
    ),
  },
  {
    path: "/locations",
    element: (
      <AppShell>
        <Locations />
      </AppShell>
    ),
  },
  {
    path: "/locations/:id",
    element: (
      <AppShell>
        <LocationDetail />
      </AppShell>
    ),
  },
  {
    path: "/suppliers",
    element: (
      <AppShell>
        <Suppliers />
      </AppShell>
    ),
  },
  {
    path: "/suppliers/:id",
    element: (
      <AppShell>
        <SupplierDetail />
      </AppShell>
    ),
  },
  {
    path: "/purchase-orders",
    element: (
      <AppShell>
        <PurchaseOrders />
      </AppShell>
    ),
  },
  {
    path: "/purchase-orders/:id",
    element: (
      <AppShell>
        <PurchaseOrderDetail />
      </AppShell>
    ),
  },
  {
    path: "/sales-orders",
    element: (
      <AppShell>
        <SalesOrders />
      </AppShell>
    ),
  },
  {
    path: "/sales-orders/:id",
    element: (
      <AppShell>
        <SalesOrderDetail />
      </AppShell>
    ),
  },
  {
    path: "/customers",
    element: (
      <AppShell>
        <Customers />
      </AppShell>
    ),
  },
  {
    path: "/customers/:id",
    element: (
      <AppShell>
        <CustomerDetail />
      </AppShell>
    ),
  },
  {
    path: "/transfers",
    element: (
      <AppShell>
        <Transfers />
      </AppShell>
    ),
  },
  {
    path: "/transfers/:id",
    element: (
      <AppShell>
        <TransferDetail />
      </AppShell>
    ),
  },
  {
    path: "/returns",
    element: (
      <AppShell>
        <Returns />
      </AppShell>
    ),
  },
  {
    path: "/returns/:id",
    element: (
      <AppShell>
        <ReturnDetail />
      </AppShell>
    ),
  },
  {
    path: "/fulfilment",
    element: (
      <AppShell>
        <Warehouse />
      </AppShell>
    ),
  },
  {
    path: "/fulfilment/pick/:id",
    element: (
      <AppShell>
        <PickSession />
      </AppShell>
    ),
  },
  {
    path: "/fulfilment/lookup",
    element: (
      <AppShell>
        <BarcodeLookup />
      </AppShell>
    ),
  },
  {
    path: "/fulfilment/packing/:id",
    element: (
      <AppShell>
        <PackingVerification />
      </AppShell>
    ),
  },
  {
    path: "/fulfilment/shipments/:id",
    element: (
      <AppShell>
        <ShipmentDetail />
      </AppShell>
    ),
  },
  { path: "/warehouse", element: <RedirectLegacyWarehouse /> },
  { path: "/warehouse/pick/:id", element: <RedirectLegacyWarehouse /> },
  { path: "/warehouse/lookup", element: <RedirectLegacyWarehouse /> },
  { path: "/warehouse/packing/:id", element: <RedirectLegacyWarehouse /> },
  { path: "/shipments", element: <RedirectLegacyShipments /> },
  { path: "/shipments/:id", element: <RedirectLegacyShipments /> },
  {
    path: "/audit",
    element: (
      <AppShell>
        <RouteGuard allow={isAdmin} title="Audit Log">
          <Audit />
        </RouteGuard>
      </AppShell>
    ),
  },
  {
    path: "/reports",
    element: (
      <AppShell>
        <RouteGuard allow={(r) => hasRole(r, "manager")} title="Reports">
          <Reports />
        </RouteGuard>
      </AppShell>
    ),
  },
  {
    path: "/settings/organization",
    element: (
      <AppShell>
        <OrganizationSettings />
      </AppShell>
    ),
  },
  {
    path: "/settings/users",
    element: (
      <AppShell>
        <UsersSettings />
      </AppShell>
    ),
  },
  {
    path: "/settings/locations",
    element: <Navigate to="/locations" replace />,
  },
  {
    path: "/settings/roles",
    element: (
      <AppShell>
        <RolesSettings />
      </AppShell>
    ),
  },
  {
    path: "/integrations",
    element: (
      <AppShell>
        <Connectors />
      </AppShell>
    ),
  },
  {
    path: "/integrations/carriers",
    element: (
      <AppShell>
        <Carriers />
      </AppShell>
    ),
  },
  {
    path: "/integrations/automation",
    element: (
      <AppShell>
        <RouteGuard allow={isAdmin} title="Sync & Events">
          <Automation />
        </RouteGuard>
      </AppShell>
    ),
  },
  {
    path: "/integrations/channels",
    element: (
      <AppShell>
        <RouteGuard allow={isAdmin} title="Channel Allocations">
          <ChannelAllocations />
        </RouteGuard>
      </AppShell>
    ),
  },
  {
    path: "/integrations/exceptions",
    element: (
      <AppShell>
        <IntegrationExceptions />
      </AppShell>
    ),
  },
  {
    path: "/settings/ai",
    element: (
      <AppShell>
        <AiControlCentre />
      </AppShell>
    ),
  },
  {
    path: "/notifications",
    element: (
      <AppShell>
        <Notifications />
      </AppShell>
    ),
  },
  {
    path: "/settings/profile",
    element: (
      <AppShell>
        <ProfileSettings />
      </AppShell>
    ),
  },
  {
    path: "/settings/preferences",
    element: (
      <AppShell>
        <PreferencesSettings />
      </AppShell>
    ),
  },
  {
    path: "/listings",
    element: (
      <AppShell>
        <Listings />
      </AppShell>
    ),
  },
  {
    path: "/listings/:id",
    element: (
      <AppShell>
        <ListingDetail />
      </AppShell>
    ),
  },
  {
    path: "/listing-templates",
    element: (
      <AppShell>
        <ListingTemplates />
      </AppShell>
    ),
  },
  {
    path: "/listing-templates/preview/:templateId",
    element: (
      <AppShell>
        <ListingTemplatePreview />
      </AppShell>
    ),
  },
  {
    path: "/listing-templates/review",
    element: (
      <AppShell>
        <ListingTemplateReview />
      </AppShell>
    ),
  },
  {
    path: "/integrations/ebay/:connectorId",
    element: (
      <AppShell>
        <EbayConnectorConfig />
      </AppShell>
    ),
  },
  {
    path: "/integrations/bigcommerce/:connectorId",
    element: (
      <AppShell>
        <BigCommerceConnectorConfig />
      </AppShell>
    ),
  },
  {
    path: "/replenishment",
    element: (
      <AppShell>
        <Replenishment />
      </AppShell>
    ),
  },
  {
    path: "/stocktake",
    element: (
      <AppShell>
        <Stocktake />
      </AppShell>
    ),
  },
  {
    path: "/reconciliation",
    element: (
      <AppShell>
        <RouteGuard allow={isAdmin} title="Marketplace Reconciliation">
          <ReconciliationCentre />
        </RouteGuard>
      </AppShell>
    ),
  },
  {
    path: "/exceptions",
    element: (
      <AppShell>
        <ExceptionCentre />
      </AppShell>
    ),
  },
  {
    path: "/automation",
    element: (
      <AppShell>
        <RouteGuard allow={isAdmin} title="Automation Rules">
          <AutomationRules />
        </RouteGuard>
      </AppShell>
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
